import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, TextInput } from 'components/Form'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Success, Error } from 'utils/alerts'
import ApiService from 'services/ApiService'

const validationSchema = Yup.object().shape({
    email: Yup.string()
        .required('Email is required')
        .email('Email is invalid')
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, 'Email is invalid'),
})

const CompleteRegistration = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [customerData, setCustomerData] = useState(null)
    const [fetchLoading, setFetchLoading] = useState(true)

    useEffect(() => {
        fetchCustomerData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const fetchCustomerData = async () => {
        try {
            setFetchLoading(true)
            const response = await ApiService.get(`/userauth/customer/${id}`)
            setCustomerData(response.data.data)
        } catch (error) {
            console.error('Error fetching customer:', error)
            Error('Failed to load customer information')
            navigate('/user')
        } finally {
            setFetchLoading(false)
        }
    }

    const handleSubmit = async (values) => {
        try {
            setLoading(true)
            const response = await ApiService.post(`/userauth/complete-registration/${id}`, {
                email: values.email
            })

            if (response.data.data.email_sent) {
                Success('Registration completed successfully! Welcome email sent to customer.')
            } else {
                Success('Registration completed successfully!')
            }
            
            navigate('/user')
        } catch (error) {
            console.error('Error completing registration:', error)
            if (error.response?.data?.data) {
                const errors = error.response.data.data
                if (typeof errors === 'object') {
                    const errorMessages = Object.values(errors).flat().join(', ')
                    Error(`Error: ${errorMessages}`)
                } else {
                    Error(`Error: ${errors}`)
                }
            } else {
                Error('Failed to complete registration. Please try again.')
            }
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        navigate('/user')
    }

    if (fetchLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading customer information...</p>
                </div>
            </div>
        )
    }

    if (!customerData) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="text-gray-600">Customer not found</p>
                    <Button onClick={() => navigate('/user')} className="mt-4">
                        Back to Users
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Complete Customer Registration
                </h2>
            </div>
            
            <div className="flex flex-col items-center">
                <div className="w-full max-w-2xl bg-white border rounded-sm border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                    {/* Customer Information */}
                    <div className="px-5 py-3 border-b border-stroke dark:border-strokedark">
                        <h3 className="font-medium text-black dark:text-white">Customer Information</h3>
                    </div>
                    
                    <div className="p-6">
                        {/* Current Customer Details */}
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg dark:bg-gray-800">
                            <h4 className="font-medium text-black dark:text-white mb-3">Current Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="font-medium text-gray-600 dark:text-gray-400">Name:</span>
                                    <p className="text-black dark:text-white">
                                        {customerData.first_name} {customerData.last_name}
                                    </p>
                                </div>
                                <div>
                                    <span className="font-medium text-gray-600 dark:text-gray-400">Phone:</span>
                                    <p className="text-black dark:text-white">
                                        {customerData.phone_number || customerData.mobile || 'Not provided'}
                                    </p>
                                </div>
                                <div>
                                    <span className="font-medium text-gray-600 dark:text-gray-400">Email:</span>
                                    <p className="text-black dark:text-white">
                                        {customerData.email || (
                                            <span className="text-yellow-600 italic">
                                                ⚠️ Not provided (incomplete registration)
                                            </span>
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <span className="font-medium text-gray-600 dark:text-gray-400">Status:</span>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        customerData.registration_status === 'complete' 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {customerData.registration_status === 'complete' ? '✓' : '⚠️'} 
                                        {' '}{customerData.registration_status?.charAt(0).toUpperCase() + customerData.registration_status?.slice(1)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Registration Completion Form */}
                        {customerData.registration_status === 'incomplete' ? (
                            <>
                                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
                                    <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                                        Complete Registration
                                    </h4>
                                    <p className="text-sm text-blue-700 dark:text-blue-300">
                                        Add an email address to complete this customer's registration. 
                                        A welcome email will be sent automatically once completed.
                                    </p>
                                </div>

                                <Formik
                                    initialValues={{
                                        email: customerData.email || ''
                                    }}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSubmit}
                                >
                                    {({ values, errors, touched }) => (
                                        <Form>
                                            <div className="mb-6">
                                                <Field name="email">
                                                    {({ field, form }) => (
                                                        <TextInput
                                                            type="email"
                                                            label="Email Address"
                                                            name="email"
                                                            error={errors.email && touched.email}
                                                            message={errors.email}
                                                            value={values.email}
                                                            onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                            placeholder="customer@example.com"
                                                        />
                                                    )}
                                                </Field>
                                            </div>

                                            <div className="flex items-center justify-end gap-4">
                                                <Button
                                                    type="button"
                                                    className="h-10 w-24 text-gray-600 border-gray-300 hover:bg-gray-50 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-800"
                                                    onClick={handleCancel}
                                                    disabled={loading}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    className="h-10 w-32 text-white bg-primary border-primary hover:bg-primary/90 dark:bg-secondary dark:border-secondary"
                                                    disabled={loading}
                                                >
                                                    {loading ? 'Completing...' : 'Complete Registration'}
                                                </Button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </>
                        ) : (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg dark:bg-green-900/20 dark:border-green-800">
                                <div className="flex items-center">
                                    <span className="text-green-600 mr-2">✓</span>
                                    <div>
                                        <h4 className="font-medium text-green-800 dark:text-green-200">
                                            Registration Already Complete
                                        </h4>
                                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                                            This customer's registration is already complete with email address: {customerData.email}
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <Button onClick={handleCancel} className="text-primary border-primary hover:bg-primary/10">
                                        Back to Users
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default CompleteRegistration
